import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Box, Typography, CircularProgress, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { apiRequest } from '../utils/api';

/**
 * Underlying stock price chart for the position-details dialog.
 * Fetches daily candles from /api/stocks/:symbol/history.
 *
 * Default duration: "since open" when the position's open date is known and
 * farther back than 90 days, otherwise 90 days.
 */
const PERIODS = [
  { key: 'open', label: 'Since Open' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: '6m', label: '6M' },
  { key: '1y', label: '1Y' },
];

const PERIOD_DAYS = { '7d': 7, '30d': 30, '90d': 90, '6m': 182, '1y': 365 };

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return Math.ceil((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function rangeForDays(days) {
  if (days <= 8) return '5d';
  if (days <= 35) return '1mo';
  if (days <= 100) return '3mo';
  if (days <= 200) return '6mo';
  if (days <= 400) return '1y';
  if (days <= 800) return '2y';
  return '5y';
}

const UnderlyingPriceChart = ({ symbol, currentPrice, openDate }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const axisColor = isDark ? '#9e9e9e' : '#757575';
  const lineColor = '#1976d2';

  const openDays = useMemo(() => daysSince(openDate), [openDate]);
  const defaultPeriod = openDays != null && openDays > 90 ? 'open' : '90d';
  const [period, setPeriod] = useState(defaultPeriod);
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPeriod(defaultPeriod);
  }, [defaultPeriod, symbol]);

  const fetchHistory = useCallback(async () => {
    if (!symbol) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const days = period === 'open' ? Math.max(openDays || 0, 30)
        : PERIOD_DAYS[period] || 90;
      const range = rangeForDays(days);
      const resp = await apiRequest(
        `/api/stocks/${encodeURIComponent(symbol)}/history?range=${range}&interval=1d`
      );
      if (resp.data?.candles) {
        const startDays = period === 'open' ? Math.max(openDays || 0, 0) : days;
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - startDays);
        const cutoffStr = cutoff.toISOString().split('T')[0];
        setCandles(resp.data.candles.filter(c => c.date >= cutoffStr));
      } else {
        setCandles([]);
        setError('No price history available');
      }
    } catch (err) {
      setError('Failed to load price history');
      setCandles([]);
    } finally {
      setLoading(false);
    }
  }, [symbol, period, openDays]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, p: 1, boxShadow: 1 }}>
        <Typography variant="caption" fontWeight="bold">{formatDate(data.date)}</Typography>
        <Typography variant="caption" display="block" sx={{ color: lineColor }}>
          ${data.close.toFixed(2)}
        </Typography>
      </Box>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 180 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (error || candles.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center', flex: 1 }}>
          <Typography variant="body2" color="text.secondary">{error || 'No price history'}</Typography>
        </Box>
      );
    }

    const prices = candles.map(c => c.close);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const pad = (max - min) * 0.05 || 1;
    const last = candles[candles.length - 1].close;
    const first = candles[0].close;
    const changePct = first ? ((last - first) / first) * 100 : 0;
    const up = last >= first;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 180 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ color: up ? 'success.main' : 'error.main' }}>
            ${last.toFixed(2)} ({changePct >= 0 ? '+' : ''}{changePct.toFixed(1)}%)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {period === 'open' && openDays != null ? `Since open (${formatDate(candles[0].date)})` : `Last ${daysSince(candles[0].date)} days`}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 180, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <ComposedChart data={candles} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <defs>
              <linearGradient id="underlyingGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={lineColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={lineColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 10 }}
              stroke={axisColor}
              minTickGap={40}
            />
            <YAxis
              domain={[min - pad, max + pad]}
              tick={{ fontSize: 10 }}
              stroke={axisColor}
              tickFormatter={(v) => `$${v.toFixed(0)}`}
              width={60}
            />
            <Tooltip content={<TooltipContent />} />
            {currentPrice != null && (
              <ReferenceLine
                y={currentPrice}
                stroke={axisColor}
                strokeDasharray="4 3"
                label={{ value: `$${Number(currentPrice).toFixed(2)}`, position: 'insideTopRight', style: { fontSize: 10, fill: axisColor } }}
              />
            )}
            <Area
              type="monotone"
              dataKey="close"
              stroke={lineColor}
              strokeWidth={2}
              fill="url(#underlyingGradient)"
              dot={false}
              name="Price"
            />
          </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 1.5, bgcolor: isDark ? 'grey.800' : 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider', flex: 1, width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{symbol}</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(e, v) => v && setPeriod(v)}
          sx={{ '& .MuiToggleButton-root': { px: 1, py: 0, fontSize: '0.65rem', textTransform: 'none' } }}
        >
          {PERIODS.filter(p => p.key !== 'open' || openDays != null).map(p => (
            <ToggleButton key={p.key} value={p.key}>
              {p.key === 'open' && openDays != null ? `Open (${openDays}d)` : p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      {renderBody()}
    </Box>
  );
};

export default UnderlyingPriceChart;
